import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  ContentLayout,
  SpaceBetween,
  Header,
  Wizard,
  Form,
  FormField,
  Input,
  Select,
  Textarea,
  FileUpload,
  Alert,
  Box,
  ColumnLayout,
  Button,
  TagEditor,
  Link
} from '@cloudscape-design/components';

import { createProject } from '../../repositories/createProject'

export function  CreateProjectPage({ jwtToken }) {
  const navigate = useNavigate();
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [projectData, setProjectData] = useState({
    name: '',
    description: '',
    team: null,
    sourceType: null,
    targetArchitecture: null,
    tags: [],
    file: null,
  });

  // Handle file upload
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadError, setUploadError] = useState('');

  const handleFileUpload = ({ detail }) => {
    const file = detail.value[0];
    if (file && !file.name.endsWith('.zip')) {
      setUploadError('Please upload a ZIP file');
      return;
    }
    setUploadedFile(file);
    setUploadError('');
  };

  const handleWizardSubmit = async () => {
    try {
      // Prepare the project data from your state
      const projectPayload = {
        name: projectData.name,
        description: projectData.description,
        team: projectData.team?.value,
        sourceType: projectData.sourceType?.value,
        targetArchitecture: projectData.targetArchitecture?.value,
        tags: projectData.tags,
        sourceCode: uploadedFile
      };
      
      const response = await createProject(projectPayload, jwtToken);
      
      // Handle successful creation
      // You might want to show a success message or redirect
      console.log('Project created successfully:', response);
      
      navigate('/jobs');
      
    } catch (error) {
      // Handle any errors
      console.error('Error creating project:', error);
      // You might want to show an error message to the user
    
      navigate('/technical-analysis');
    }
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="Create a new modernization project"
        >
          Create project
        </Header>
      }
    >
      <Wizard
        activeStepIndex={activeStepIndex}
        onNavigate={({ detail }) => setActiveStepIndex(detail.requestedStepIndex)}
        onSubmit={handleWizardSubmit}
        isLoadingNextStep={false} 
        steps={[
          {
            title: 'Project details',
            description: 'Provide basic project information',
            content: (
              <Container
                header={
                  <Header variant="h2">
                    Project details
                  </Header>
                }
              >
                <SpaceBetween size="l">
                  <FormField
                    label="Project name"
                    description="A unique name for your modernization project"
                    constraintText="Must be unique, 3-63 characters, start with a letter"
                  >
                    <Input
                      value={projectData.name}
                      onChange={({ detail }) => 
                        setProjectData(prev => ({ ...prev, name: detail.value }))
                      }
                    />
                  </FormField>

                  <FormField
                    label="Description"
                    description="Describe the purpose and scope of this modernization"
                  >
                    <Textarea
                      value={projectData.description}
                      onChange={({ detail }) =>
                        setProjectData(prev => ({ ...prev, description: detail.value }))
                      }
                    />
                  </FormField>

                  <FormField
                    label="Team"
                    description="Select the team responsible for this modernization"
                  >
                    <Select
                      selectedOption={projectData.team}
                      onChange={({ detail }) =>
                        setProjectData(prev => ({ ...prev, team: detail.selectedOption }))
                      }
                      options={[
                        { label: 'Team Alpha', value: 'alpha' },
                        { label: 'Team Beta', value: 'beta' },
                        { label: 'Team Gamma', value: 'gamma' }
                      ]}
                    />
                  </FormField>

                  <FormField
                    label="Tags"
                    description="Add tags to help organize and track your project"
                  >
                    <TagEditor
                      tags={projectData.tags}
                      onChange={({ detail }) =>
                        setProjectData(prev => ({ ...prev, tags: detail.tags }))
                      }
                    />
                  </FormField>
                </SpaceBetween>
              </Container>
            )
          },
          {
            title: 'Source configuration',
            description: 'Configure source application details',
            content: (
              <Container
                header={
                  <Header variant="h2">
                    Source configuration
                  </Header>
                }
              >
                <SpaceBetween size="l">
                  <FormField
                    label="Source technology"
                    description="Select the primary technology of your monolithic application"
                  >
                    <Select
                      selectedOption={projectData.sourceType}
                      onChange={({ detail }) =>
                        setProjectData(prev => ({ ...prev, sourceType: detail.selectedOption }))
                      }
                      options={[
                        { label: 'Java Spring', value: 'java-spring' },
                        { label: 'Java EE', value: 'java-ee' },
                        { label: '.NET Framework', value: 'dotnet' },
                        { label: 'Python Django', value: 'django' }
                      ]}
                    />
                  </FormField>

                  <FormField
                    label="Target architecture"
                    description="Select the desired microservices architecture"
                  >
                    <Select
                      selectedOption={projectData.targetArchitecture}
                      onChange={({ detail }) =>
                        setProjectData(prev => ({ 
                          ...prev, 
                          targetArchitecture: detail.selectedOption 
                        }))
                      }
                      options={[
                        { label: 'Spring Boot Microservices', value: 'spring-boot' },
                        { label: '.NET Core Microservices', value: 'dotnet-core' },
                        { label: 'Node.js Microservices', value: 'nodejs' }
                      ]}
                    />
                  </FormField>

                  <Alert type="info">
                    Make sure your source code follows the standard project structure
                    for accurate analysis. <Link>Learn more</Link>
                  </Alert>
                </SpaceBetween>
              </Container>
            )
          },
          {
            title: 'Upload source code',
            description: 'Upload your application source code',
            content: (
              <Container
                header={
                  <Header variant="h2">
                    Upload source code
                  </Header>
                }
              >
                <SpaceBetween size="l">
                  <Alert type="info">
                    Please upload a ZIP file containing your application source code.
                    The ZIP file should include the complete project structure.
                  </Alert>

                  <FormField
                    label="Source code"
                    description="Upload your application source code as a ZIP file"
                    errorText={uploadError}
                  >
                    <FileUpload
                      onChange={handleFileUpload}
                      value={uploadedFile ? [uploadedFile] : []}
                      accept=".zip"
                      multiple={false}
                      constraintText="File size limit: 500MB"
                    />
                  </FormField>

                  <ColumnLayout columns={2}>
                    <Box>
                      <SpaceBetween size="xs">
                        <Box variant="strong">Requirements:</Box>
                        <Box variant="small">• ZIP file format</Box>
                        <Box variant="small">• Complete project structure</Box>
                        <Box variant="small">• Source code only (no binaries)</Box>
                      </SpaceBetween>
                    </Box>
                    <Box>
                      <SpaceBetween size="xs">
                        <Box variant="strong">Recommended structure:</Box>
                        <Box variant="small">• /src directory</Box>
                        <Box variant="small">• Build configuration files</Box>
                        <Box variant="small">• Dependencies list</Box>
                      </SpaceBetween>
                    </Box>
                  </ColumnLayout>
                </SpaceBetween>
              </Container>
            )
          },
          {
            title: 'Review and create',
            description: 'Review project configuration',
            content: (
              <Container
                header={
                  <Header variant="h2">
                    Review project configuration
                  </Header>
                }
              >
                <SpaceBetween size="l">
                  <ColumnLayout columns={2}>
                    <Box>
                      <SpaceBetween size="s">
                        <Box variant="h3">Project details</Box>
                        <FormField label="Project name">
                          <Box>{projectData.name}</Box>
                        </FormField>
                        <FormField label="Team">
                          <Box>{projectData.team?.label}</Box>
                        </FormField>
                        <FormField label="Description">
                          <Box>{projectData.description}</Box>
                        </FormField>
                      </SpaceBetween>
                    </Box>
                    <Box>
                      <SpaceBetween size="s">
                        <Box variant="h3">Configuration</Box>
                        <FormField label="Source technology">
                          <Box>{projectData.sourceType?.label}</Box>
                        </FormField>
                        <FormField label="Target architecture">
                          <Box>{projectData.targetArchitecture?.label}</Box>
                        </FormField>
                        <FormField label="Source code">
                          <Box>{uploadedFile?.name || 'No file uploaded'}</Box>
                        </FormField>
                      </SpaceBetween>
                    </Box>
                  </ColumnLayout>

                  <Alert type="info">
                    Review the configuration above. Once you create the project,
                    the technical analysis will begin automatically.
                  </Alert>
                </SpaceBetween>
              </Container>
            )
          }
        ]}
        i18nStrings={{
          stepNumberLabel: stepNumber => `Step ${stepNumber}`,
          collapsedStepsLabel: (stepNumber, stepsCount) =>
            `Step ${stepNumber} of ${stepsCount}`,
          cancelButton: 'Cancel',
          previousButton: 'Previous',
          nextButton: 'Next',
          submitButton: 'Create project'
        }}
      />
    </ContentLayout>
  );
};
